import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MASCOT_IMG } from "../../data/constants";

const QUESTIONS = [
  "Have you eaten a salad in the last decade?",
  "Do you own a bathroom?",
  "Are you currently... vibrating?",
  "Have you ever trusted a raspberry?",
  "Is your stomach making noises right now?",
];

const DIAGNOSES = [
  { level: "TERMINALLY INFECTED", note: "Prognosis: explosive. Recommend relocating your desk to the bathroom.", color: "#b026ff" },
  { level: "MAXIMUM OOCYST LOAD", note: "You are 98% parasite by volume. Congrats, you're basically a petri dish.", color: "#d9b3ff" },
  { level: "CODE BROWN IMMINENT", note: "Sprint speed detected: Olympic. Toilet proximity: critical.", color: "#b026ff" },
  { level: "PATIENT ZERO ENERGY", note: "You're not sick, you're the outbreak. Everyone run.", color: "#d9b3ff" },
];

export const SymptomChecker = () => {
  const [answers, setAnswers] = useState({});
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [meter, setMeter] = useState(50);

  const diagnose = () => {
    setScanning(true);
    setResult(null);
    setTimeout(() => {
      setScanning(false);
      const d = DIAGNOSES[Math.floor(Math.random() * DIAGNOSES.length)];
      setResult(d);
      toast("Results are in!", { description: "Spoiler: it's not good." });
    }, 2200);
  };

  return (
    <section id="diagnose" className="relative px-4 py-16 md:py-24 max-w-5xl mx-auto" data-testid="diagnose-section">
      <div className="text-center mb-10">
        <h2 className="stencil text-4xl md:text-6xl">SELF-DIAGNOSIS KIOSK</h2>
        <p className="font-mono2 text-sm text-white/70 mt-3">Not FDA approved. Not approved by anyone. Answer honestly (results are always the same).</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* quiz */}
        <div className="petri p-6" data-testid="diagnose-quiz">
          <h3 className="stencil text-xl mb-4 flex items-center gap-2"><Activity className="text-[#b026ff]" size={22} /> SYMPTOM SCAN</h3>
          <div className="flex flex-col gap-3">
            {QUESTIONS.map((q, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-2 border-[#b026ff]/40 bg-[#0a133f]/60 px-3 py-2">
                <span className="font-mono2 text-xs text-white/85">{q}</span>
                <div className="flex gap-1 shrink-0">
                  {["YES", "NO"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAnswers({ ...answers, [i]: opt })}
                      data-testid={`quiz-${i}-${opt.toLowerCase()}`}
                      className={`font-mono2 text-[10px] px-2 py-1 border-2 border-[#0a133f] ${
                        answers[i] === opt ? "bg-[#d9b3ff] text-[#0a133f]" : "bg-white/10 text-white/70"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button onClick={diagnose} disabled={scanning} data-testid="diagnose-btn" className="btn-hazard bg-[#b026ff] text-white w-full mt-5 py-3 text-lg flex items-center justify-center gap-2 disabled:opacity-70">
            {scanning ? <><Loader2 className="animate-spin" size={18} /> ANALYZING STOOL...</> : "DIAGNOSE ME"}
          </button>

          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mt-4 border-2 p-4 text-center" style={{ borderColor: result.color }} data-testid="diagnose-result">
                <img src={MASCOT_IMG} alt="" className="w-16 h-16 mx-auto wobble" />
                <p className="stencil text-2xl mt-2" style={{ color: result.color, WebkitTextStroke: "1.5px #0a133f" }}>{result.level}</p>
                <p className="font-mono2 text-xs text-white/80 mt-2">{result.note}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* poop-o-meter */}
        <div className="petri p-6 flex flex-col" data-testid="poop-meter">
          <h3 className="stencil text-xl mb-4">THE POOP-O-METER™</h3>
          <p className="font-mono2 text-xs text-white/70 mb-6">Drag the slider to simulate your current intestinal situation. For science.</p>

          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative w-36 h-64 border-4 border-[#0a133f] bg-[#0a133f]/60 overflow-hidden mb-6">
              <motion.div
                className="absolute bottom-0 left-0 right-0"
                style={{ background: "linear-gradient(#d9b3ff, #b026ff)" }}
                animate={{ height: `${meter}%` }}
                transition={{ type: "spring", stiffness: 120 }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="stencil text-4xl" style={{ WebkitTextStroke: "2px #0a133f" }}>{meter}%</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={meter}
              onChange={(e) => setMeter(Number(e.target.value))}
              data-testid="poop-slider"
              className="w-full accent-[#b026ff]"
            />
            <p className="font-mono2 text-xs text-center mt-4" style={{ color: meter > 75 ? "#b026ff" : "#d9b3ff" }}>
              {meter < 25 && "Suspiciously stable. Are you even alive?"}
              {meter >= 25 && meter < 50 && "Rumbling detected. Locate nearest exit."}
              {meter >= 50 && meter < 75 && "DEFCON 2. Do not sneeze."}
              {meter >= 75 && "EVACUATE. THIS IS NOT A DRILL."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SymptomChecker;
